import type { LinkProps as SystemLinkProps } from '@hover/blueprint';
import {
  Link as SystemLink,
  forwardRef as systemForwardRef,
} from '@hover/blueprint';
import { captureException } from '@sentry/react';
import type { MouseEventHandler } from 'react';
import React, { forwardRef } from 'react';
import type { LinkProps as RouterLinkProps } from 'react-router-dom';
import {
  Link as RouterLink,
  useLocation as useRouterLocation,
} from 'react-router-dom';
import invariant from 'tiny-invariant';

import { useCurrentPage, useLocation, useMode } from 'src/shared/hooks';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import type { LinkPressed } from 'src/shared/telemetry';
import { linkPressed } from 'src/shared/telemetry';

type TrackHandler = (
  properties: LinkPressed & { button_or_link: string; button_text: string },
) => void;

export interface LinkProps
  extends SystemLinkProps,
    Omit<RouterLinkProps, 'children' | 'color'> {
  linkLocation?: string;
  pageName?: string;
  preserveQuery?: boolean;
  primaryCallToAction?: boolean;
  track?: TrackHandler;
  optionalTrackingProperties?: Partial<LinkPressed>;
  isExternal?: boolean;
  target?: string;
}

interface InnerLinkProps extends Omit<LinkProps, 'to'> {
  /**
   * React Router passes this function in to the custom link component
   */
  navigate?: () => void;
}

/**
 * Custom link component to be passed as the `component` prop to React Router's
 * link. This is necessary in order to make a tracking request on navigation.
 */
const InnerLink = forwardRef<HTMLAnchorElement, InnerLinkProps>(
  (
    {
      children,
      href,
      navigate,
      pageName,
      primaryCallToAction = false,
      track,
      linkLocation,
      optionalTrackingProperties = {},
      isExternal,
      target,
      ...props
    },
    ref,
  ) => {
    const currentPage = useCurrentPage();
    const page = pageName || currentPage;
    const linkText =
      typeof children === 'string' ? children : props['aria-label'];
    const mode = useMode();

    const {
      metadata: { partner_id: partnerId },
    } = useTrackingMetadata();

    const onLinkClick: MouseEventHandler<HTMLAnchorElement> = event => {
      event.preventDefault();
      props.onClick?.(event);

      try {
        invariant(page, 'Link: pageName is required when current page is null');
        invariant(
          linkText,
          'Link: label is required when children is not string',
        );

        const properties = {
          page_or_screen_name: page,
          primary_cta: primaryCallToAction,
          link_location: linkLocation,
          link_text: linkText,
          design_studio_mode: mode,
          partner_id: partnerId,
          ...optionalTrackingProperties,
        };

        if (track) {
          track({
            button_or_link: 'link',
            button_text: linkText,
            ...properties,
          });
        } else {
          linkPressed(properties);
        }
      } catch (error) {
        captureException(error);
      }

      if (navigate) {
        navigate();
      } else if (href) {
        window.open(href, target || (isExternal ? '_blank' : '_self'));
      }
    };

    return (
      <SystemLink
        {...props}
        href={href}
        onPress={onLinkClick}
        ref={ref}
        rel="noopener noreferrer"
        target={target}
      >
        {children}
      </SystemLink>
    );
  },
);

const Link = systemForwardRef<LinkProps, 'a'>(
  ({ color, preserveQuery, isExternal, to, ...props }, ref) => {
    const { search } = useLocation();
    const location = useRouterLocation();

    const buildToWithQuery = () => {
      if (typeof to === 'string') {
        return to + search;
      }

      if (typeof to === 'function') {
        const descriptor = to(location);

        return typeof descriptor === 'string'
          ? descriptor + search
          : { ...descriptor, search };
      }

      return { ...to, search };
    };

    const href = preserveQuery ? buildToWithQuery() : to;

    if (isExternal) {
      return (
        <InnerLink
          {...props}
          color={color}
          href={href as string}
          isExternal
          ref={ref}
        />
      );
    }

    return (
      <RouterLink
        {...props}
        // React Router passes all props to the `InnerLink`, but its type
        // definitions do not merge the inner link component's props. Instead it
        // simply expects props for an anchor tag. The anchor tag's `color` prop
        // conflicts with Blueprint's `color` prop.
        color={color as string}
        component={InnerLink}
        ref={ref}
        to={href}
      />
    );
  },
);

export { Link };
